import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, Button, Row, Col } from "reactstrap";
import AddonSelector from "./AddonSelector";

const POSAddonModal = ({
  modal,
  toggleModal,
  product,
  addonQuantities,
  setAddonQuantities,
  handleAddToCartWithAddons,
  isEditMode = false,
}) => {
  const [subtotal, setSubtotal] = useState(parseFloat(product.price) || 0);
  const [currentAddonIndex, setCurrentAddonIndex] = useState(0);
  const [addonOrder, setAddonOrder] = useState(product.addons || []);
  const [customOptions, setCustomOptions] = useState({});

  // Filter addons based on visibility conditions
  const getVisibleAddons = useCallback(() => {
    return addonOrder.filter((addon) => {
      if (!addon.visibility_condition) return true;
      const conditions = typeof addon.visibility_condition === "string" 
        ? JSON.parse(addon.visibility_condition) 
        : addon.visibility_condition;
      if (!Array.isArray(conditions) || conditions.length === 0) return true;
      return conditions.some((condition) => {
        const sourceAddon = addonOrder.find((a) => a.data_name === condition.field);
        if (!sourceAddon) return false;
        const selected = addonQuantities[sourceAddon.id] || {};
        return selected[condition.value] > 0;
      });
    });
  }, [addonOrder, addonQuantities]);

  const visibleAddons = getVisibleAddons();
  const currentAddon = visibleAddons[currentAddonIndex];

  useEffect(() => {
    setAddonOrder(product.addons || []);
  }, [product.addons]);

  const handleChange = (addon, option, isCustom = false) => {
    const addonId = addon.id;
    const optionId = option.id;

    if (isCustom) {
      setCustomOptions((prev) => ({
        ...prev,
        [optionId]: option,
      }));
    }

    setAddonQuantities((prev) => {
      const updated = { ...prev };
      if (addon.is_multiple) {
        const currentQty = updated[addonId]?.[optionId] || 0;
        updated[addonId] = {
          ...(updated[addonId] || {}),
          [optionId]: currentQty + 1,
        };
      } else {
        updated[addonId] = { [optionId]: option.isCustom ? { quantity: 1, customText: option.option } : 1 };
      }
      return updated;
    });
  };

  const handleDeselect = (addon, option, removeAll = false) => {
    setAddonQuantities((prev) => {
      const updated = { ...prev };
      if (updated[addon.id]) {
        const currentQty = updated[addon.id][option.id] || 0;
        if (removeAll || currentQty <= 1) {
          const { [option.id]: _, ...rest } = updated[addon.id];
          updated[addon.id] = rest;
        } else {
          updated[addon.id] = { ...updated[addon.id], [option.id]: currentQty - 1 };
        }
      }
      return updated;
    });
  };

  const SIZE_FLAVOR_MAP = { small: 1, medium: 2, large: 3 };

  const getFlavorLimit = () => {
    if (!addonOrder.length) return null;
    const firstAddon = addonOrder[0];
    const firstTitle = firstAddon.title?.toLowerCase() || "";
    const selected = addonQuantities[firstAddon.id] || {};
    const selectedOptId = Object.keys(selected).find((id) => selected[id]);
    if (!selectedOptId) return null;
    const selectedOpt = firstAddon.options.find((o) => o.id === selectedOptId);
    if (!selectedOpt) return null;
    const optionName = selectedOpt.option?.toLowerCase().trim() || "";

    if (firstTitle.includes("scoop")) {
      const num = parseInt(selectedOpt.option);
      return isNaN(num) ? null : num;
    }

    if (firstTitle.includes("size")) {
      return SIZE_FLAVOR_MAP[optionName] || null;
    }

    return null;
  };

  useEffect(() => {
    let total = parseFloat(product.price) || 0;
    addonOrder.forEach((addon) => {
      const selectedOptions = addonQuantities[addon.id] || {};
      Object.keys(selectedOptions).forEach((optionId) => {
        const option = addon.options.find((o) => o.id === optionId) || customOptions[optionId];
        const qty = selectedOptions[optionId] || 0;
        if (option && qty) {
          total += parseFloat(option.price || 0) * qty;
        }
      });
    });
    setSubtotal(total);
  }, [addonQuantities, product, addonOrder, customOptions]);

  const validateRequiredFields = useCallback(() => {
    for (const addon of visibleAddons) {
      if (addon.is_required) {
        const selectedOptions = addonQuantities[addon.id] || {};
        const hasSelection = Object.keys(selectedOptions).some(
          (optionId) => selectedOptions[optionId]
        );
        if (!hasSelection) {
          toast.error(`Please select a required option for "${addon.title}".`);
          return false;
        }
      }
    }
    return true;
  }, [visibleAddons, addonQuantities]);

  const prepareAddonData = useCallback(() => {
    const formattedAddons = {};
    addonOrder.forEach((addon) => {
      const selectedOptions = addonQuantities[addon.id] || {};
      const selected = Object.keys(selectedOptions)
        .filter((optionId) => selectedOptions[optionId])
        .map((optionId) => {
          const option = addon.options.find((o) => o.id === optionId) || customOptions[optionId];
          const qty = selectedOptions[optionId] || 1;
          return option
            ? `${option.option}${qty > 1 ? ` x${qty}` : ""}${
                option.price ? ` (+$${(parseFloat(option.price) * qty).toFixed(2)})` : ""
              }`
            : null;
        })
        .filter(Boolean)
        .join(", ");
      if (selected) {
        formattedAddons[addon.title] = selected;
      }
    });
    return JSON.stringify(formattedAddons);
  }, [addonOrder, addonQuantities, customOptions]);

  const handleAddToCart = useCallback(() => {
    if (!validateRequiredFields()) return;
    const selectedAddons = prepareAddonData();
    handleAddToCartWithAddons(selectedAddons, subtotal);
  }, [validateRequiredFields, prepareAddonData, handleAddToCartWithAddons, subtotal]);

  const goToPrev = () => setCurrentAddonIndex(prev => Math.max(prev - 1, 0));
  const goToNext = () => setCurrentAddonIndex(prev => Math.min(prev + 1, visibleAddons.length - 1));

  return (
    <Modal
      isOpen={modal}
      toggle={toggleModal}
      size="lg"
      centered
      style={{ border: "none" }}
      contentClassName="bg-dark text-white"
    >
      <ModalBody style={{ backgroundColor: "#000", color: "#fff" }}>
        {isEditMode && visibleAddons.length > 1 && (
          <div
            className="d-flex align-items-center gap-2 mb-3 pb-3"
            style={{ borderBottom: "1px solid #333", flexWrap: "wrap" }}
          >
            <Button
              size="sm"
              color="secondary"
              outline
              onClick={goToPrev}
              disabled={currentAddonIndex === 0}
              style={{ padding: "4px 10px", minWidth: "32px" }}
            >
              ←
            </Button>

            <div className="d-flex gap-2 flex-wrap flex-grow-1 justify-content-center">
              {visibleAddons.map((addon, idx) => {
                const hasSelection = Object.values(addonQuantities[addon.id] || {}).some(v => v);
                const isActive = idx === currentAddonIndex;
                return (
                  <div
                    key={addon.id}
                    onClick={() => setCurrentAddonIndex(idx)}
                    style={{
                      cursor: "pointer",
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: "13px",
                      fontWeight: isActive ? "700" : "400",
                      border: isActive ? "2px solid #ff9800" : "1px solid #555",
                      color: isActive ? "#ff9800" : hasSelection ? "#28a745" : "#aaa",
                      background: isActive ? "rgba(255,152,0,0.1)" : "transparent",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {hasSelection && !isActive && <span style={{ marginRight: "4px" }}>✓</span>}
                    {addon.title}
                  </div>
                );
              })}
            </div>

            <Button
              size="sm"
              color="secondary"
              outline
              onClick={goToNext}
              disabled={currentAddonIndex === visibleAddons.length - 1}
              style={{ padding: "4px 10px", minWidth: "32px" }}
            >
              →
            </Button>
          </div>
        )}

        <Row>
          <Col md="12">
            {currentAddon && (
              <AddonSelector
                addon={currentAddon}
                productId={product.id}
                onSelect={handleChange}
                onDeselect={handleDeselect}
                onNext={() => {
                  if (isEditMode) {
                    goToNext();
                  } else if (currentAddonIndex >= visibleAddons.length - 1) {
                    handleAddToCart();
                  } else {
                    setCurrentAddonIndex(prev => prev + 1);
                  }
                }}
                onBack={goToPrev}
                onDone={handleAddToCart}
                showBack={isEditMode ? currentAddonIndex > 0 : currentAddonIndex > 0}
                selectedOptions={addonQuantities}
                maxQuantity={currentAddonIndex > 0 ? getFlavorLimit() : null}
                disableAutoAdvance={isEditMode}
              />
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter style={{ backgroundColor: "#000", color: "#fff", border: "none" }}>
        {isEditMode && (
          <Button color="warning" onClick={handleAddToCart}>
            Update Cart
          </Button>
        )}
        <Button color="danger" onClick={toggleModal}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default POSAddonModal;
